import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Avatar,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Progress,
  Icon,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Input,
  FormControl,
  FormLabel,
  useToast,
  Flex,
} from '@chakra-ui/react';
import {
  FiCalendar,
  FiTarget,
  FiActivity,
  FiEdit,
  FiCamera
} from 'react-icons/fi';
import { useAuth } from '../../hooks/useAuth';
import { userService } from '../../services/userService';
import { imageService } from '../../services/imageService';
import { useDataStore } from '../../contexts/DataStoreContext';
import { LoadingSpinner } from '../Common/LoadingSpinner';

export const UserProfile = ({ userId: propUserId }) => {
  const { user } = useAuth();
  const dataStore = useDataStore();
  const userId = propUserId || user?.uid;
  const isOwnProfile = !propUserId || propUserId === user?.uid;

  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [rank, setRank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editForm, setEditForm] = useState({ displayName: '', bio: '', profilePicture: null });
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    if (userId && !dataStore.isLoading) {
      loadUserData();
    }
  }, [userId, dataStore.isLoading, dataStore.votes, dataStore.fights]);

  // Update profile when DataStore changes
  useEffect(() => {
    if (userId) {
      const userProfile = dataStore.getUser(userId);
      if (userProfile && (!profile || profile.updatedAt !== userProfile.updatedAt)) {
        setProfile(userProfile);
        setEditForm({
          displayName: userProfile.displayName || '',
          bio: userProfile.bio || '',
          profilePicture: userProfile.profilePicture || null
        });
      }
    }
  }, [userId, dataStore.users, profile]);

  const loadUserData = () => {
    try {
      setLoading(true);

      // Wait for DataStore to be loaded before accessing data
      if (dataStore.isLoading) {
        return;
      }

      // Get profile from DataStore (real-time)
      const profileData = dataStore.getUser(userId);

      // Get complex analytics from DataStore (computed from in-memory data)
      const statsData = dataStore.getUserVotingStats(userId);
      const rankData = dataStore.getUserRank(userId);

      if (profileData) {
        setProfile(profileData);
        setEditForm({
          displayName: profileData.displayName || '',
          bio: profileData.bio || '',
          profilePicture: profileData.profilePicture || null
        });
      }

      setStats(statsData);
      setRank(rankData);
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditProfile = () => {
    // Reset form and preview when opening modal
    setProfilePictureFile(null);
    setProfilePicturePreview(null);
    onOpen();
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validation = imageService.validateImageFile(file);
    if (!validation.isValid) {
      toast({
        title: 'Invalid image file',
        description: validation.error,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setProfilePictureFile(file);
    const previewUrl = imageService.createPreviewUrl(file);
    setProfilePicturePreview(previewUrl);
  };

  const handleRemoveProfilePicture = () => {
    if (profilePicturePreview) {
      imageService.revokePreviewUrl(profilePicturePreview);
    }
    setProfilePictureFile(null);
    setProfilePicturePreview(null);
  };

  const handleSaveProfile = async () => {
    try {
      setUploadingPicture(true);
      let profilePictureUrl = editForm.profilePicture;

      // Upload new profile picture if selected
      if (profilePictureFile) {
        const timestamp = Date.now();
        const fileExtension = profilePictureFile.name.split('.').pop();
        const fileName = `profile_${timestamp}.${fileExtension}`;
        const path = `users/${userId}/profile/${fileName}`;

        profilePictureUrl = await imageService.uploadImage(profilePictureFile, path);
      }

      await userService.updateUserProfile(userId, {
        displayName: editForm.displayName,
        bio: editForm.bio,
        profilePicture: profilePictureUrl
      });

      toast({
        title: 'Profile updated successfully!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Clean up preview URL
      if (profilePicturePreview) {
        imageService.revokePreviewUrl(profilePicturePreview);
      }

      // DataStore will automatically update via real-time subscription
      // Only reload analytics data from in-memory data
      const statsData = dataStore.getUserVotingStats(userId);
      const rankData = dataStore.getUserRank(userId);
      setStats(statsData);
      setRank(rankData);

      onClose();
    } catch (error) {
      toast({
        title: 'Failed to update profile',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setUploadingPicture(false);
    }
  };

  const getAccuracyColor = (percentage) => {
    if (percentage >= 70) return 'green';
    if (percentage >= 50) return 'yellow';
    return 'red';
  };


  if (loading) {
    return <LoadingSpinner message="Loading profile..." />;
  }

  if (!profile) {
    return (
      <Box textAlign="center" py={10}>
        <Text color="gray.400">Profile not found</Text>
      </Box>
    );
  }

  return (
    <Box
      maxW="6xl"
      mx="auto"
      px={{ base: 4, md: 6 }}
      py={{ base: 6, md: 8 }}
      my={{ base: 8, md: 12 }}
      minH="calc(100vh - 64px)"
    >
      {/* Profile Header */}
      <Card mb={{ base: 6, md: 8 }}>
        <CardBody p={{ base: 4, md: 6 }}>
          <VStack spacing={{ base: 4, md: 6 }} align="stretch">
            {/* Mobile: Stack vertically, Desktop: Side by side */}
            <Flex
              direction={{ base: 'column', md: 'row' }}
              align={{ base: 'center', md: 'start' }}
              spacing={{ base: 4, md: 6 }}
              gap={{ base: 4, md: 6 }}
            >
              <Avatar
                size={{ base: 'xl', md: '2xl' }}
                name={profile.displayName || 'User'}
                src={profile.profilePicture}
                color="gray.700"
                bg="white"
                flexShrink={0}
              />

              <VStack align={{ base: 'center', md: 'start' }} spacing={3} flex={1} w="full">
                <VStack align={{ base: 'center', md: 'start' }} spacing={1} w="full">
                  <Heading
                    size={{ base: 'lg', md: 'xl' }}
                    color="white"
                    textAlign={{ base: 'center', md: 'left' }}
                  >
                    {profile.displayName || 'Anonymous User'}
                  </Heading>
                  <Text
                    color="gray.400"
                    fontSize="sm"
                    textAlign={{ base: 'center', md: 'left' }}
                  >
                    Member since {profile.createdAt?.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long'
                    })}
                  </Text>
                </VStack>

                {profile.bio && (
                  <Text
                    color="gray.300"
                    maxW="md"
                    textAlign={{ base: 'center', md: 'left' }}
                  >
                    {profile.bio}
                  </Text>
                )}

                {rank && (
                  <HStack spacing={4} justify={{ base: 'center', md: 'flex-start' }}>
                    <Badge colorScheme="blue" variant="solid">
                      Rank #{rank.rank}
                    </Badge>
                    <Text fontSize="sm" color="gray.400">
                      Top {rank.percentile?.toFixed(1)}% of users
                    </Text>
                  </HStack>
                )}
              </VStack>
            </Flex>

            {/* Edit Profile Button */}
            {isOwnProfile && (
              <Flex justify={{ base: 'center', md: 'flex-end' }} w="full">
                <Button
                  leftIcon={<FiEdit />}
                  size="sm"
                  variant="outline"
                  onClick={handleEditProfile}
                >
                  Edit Profile
                </Button>
              </Flex>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Statistics Section */}
      <VStack spacing={{ base: 6, md: 8 }} align="stretch">
        {/* Key Stats */}
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 4, md: 6 }}>
          <Card>
            <CardBody textAlign="center" p={{ base: 4, md: 6 }}>
              <Stat>
                <StatLabel color="white" fontSize={{ base: 'sm', md: 'md' }}>Total Votes</StatLabel>
                <StatNumber color="white" fontSize={{ base: 'xl', md: '2xl' }}>{stats?.totalVotes || 0}</StatNumber>
                <StatHelpText fontSize={{ base: 'xs', md: 'sm' }}>
                  <Icon as={FiActivity} mr={1} />
                  All time
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody textAlign="center" p={{ base: 4, md: 6 }}>
              <Stat>
                <StatLabel color="white" fontSize={{ base: 'sm', md: 'md' }}>Accuracy</StatLabel>
                <StatNumber
                  color={`${getAccuracyColor(stats?.accuracy?.percentage || 0)}.400`}
                  fontSize={{ base: 'xl', md: '2xl' }}
                >
                  {stats?.accuracy?.percentage?.toFixed(1) || 0}%
                </StatNumber>
                <StatHelpText fontSize={{ base: 'xs', md: 'sm' }}>
                  {stats?.accuracy?.correctPredictions || 0}/{stats?.accuracy?.totalCompletedFights || 0} correct
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody textAlign="center" p={{ base: 4, md: 6 }}>
              <Stat>
                <StatLabel color="white" fontSize={{ base: 'sm', md: 'md' }}>Events</StatLabel>
                <StatNumber color="white" fontSize={{ base: 'xl', md: '2xl' }}>{stats?.eventsParticipated || 0}</StatNumber>
                <StatHelpText fontSize={{ base: 'xs', md: 'sm' }}>
                  <Icon as={FiCalendar} mr={1} />
                  Participated
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody textAlign="center" p={{ base: 4, md: 6 }}>
              <Stat>
                <StatLabel color="white" fontSize={{ base: 'sm', md: 'md' }}>Fights</StatLabel>
                <StatNumber color="white" fontSize={{ base: 'xl', md: '2xl' }}>{stats?.fightsVotedOn || 0}</StatNumber>
                <StatHelpText fontSize={{ base: 'xs', md: 'sm' }}>
                  <Icon as={FiTarget} mr={1} />
                  Voted on
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Accuracy Breakdown */}
        {stats?.accuracy && stats.accuracy.totalCompletedFights > 0 && (
          <Card>
            <CardHeader p={{ base: 4, md: 6 }}>
              <Heading size={{ base: 'sm', md: 'md' }} color="white">Prediction Accuracy</Heading>
            </CardHeader>
            <CardBody p={{ base: 4, md: 6 }}>
              <VStack spacing={{ base: 4, md: 6 }}>
                <Box w="full">
                  <HStack justify="space-between" mb={2}>
                    <Text color="gray.300" fontSize={{ base: 'sm', md: 'md' }}>Overall Accuracy</Text>
                    <Text color="white" fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>
                      {stats.accuracy.percentage.toFixed(1)}%
                    </Text>
                  </HStack>
                  <Progress
                    value={stats.accuracy.percentage}
                    colorScheme={getAccuracyColor(stats.accuracy.percentage)}
                    size={{ base: 'md', md: 'lg' }}
                    borderRadius="md"
                  />
                </Box>

                <SimpleGrid columns={2} spacing={{ base: 3, md: 4 }} w="full">
                  <Box textAlign="center">
                    <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold" color="green.400">
                      {stats.accuracy.correctPredictions}
                    </Text>
                    <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.400">Correct Predictions</Text>
                  </Box>
                  <Box textAlign="center">
                    <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold" color="red.400">
                      {stats.accuracy.totalCompletedFights - stats.accuracy.correctPredictions}
                    </Text>
                    <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.400">Incorrect Predictions</Text>
                  </Box>
                </SimpleGrid>
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* Favorite Weight Classes */}
        {stats?.favoriteWeightClasses?.length > 0 && (
          <Card>
            <CardHeader p={{ base: 4, md: 6 }}>
              <Heading size={{ base: 'sm', md: 'md' }} color="white">Favorite Weight Classes</Heading>
            </CardHeader>
            <CardBody p={{ base: 4, md: 6 }}>
              <VStack spacing={{ base: 2, md: 3 }} align="stretch">
                {stats.favoriteWeightClasses.map((weightClass, index) => (
                  <HStack key={weightClass.weightClass} justify="space-between">
                    <HStack spacing={{ base: 2, md: 3 }}>
                      <Badge colorScheme="blue" variant="solid" fontSize={{ base: 'xs', md: 'sm' }}>
                        #{index + 1}
                      </Badge>
                      <Text color="white" fontSize={{ base: 'sm', md: 'md' }}>{weightClass.weightClass}</Text>
                    </HStack>
                    <Text color="gray.300" fontSize={{ base: 'sm', md: 'md' }}>{weightClass.count} votes</Text>
                  </HStack>
                ))}
              </VStack>
            </CardBody>
          </Card>
        )}
      </VStack>

      {/* Edit Profile Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Profile</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={6}>
              {/* Profile Picture Section */}
              <FormControl>
                <FormLabel>Profile Picture</FormLabel>
                <VStack spacing={4}>
                  <Avatar
                    size="2xl"
                    src={profilePicturePreview || profile?.profilePicture}
                    name={editForm.displayName || 'User'}
                    color="gray.700"
                    bg="white"
                  />

                  <HStack spacing={3}>
                    <Button
                      leftIcon={<FiCamera />}
                      size="sm"
                      variant="outline"
                      onClick={() => document.getElementById('profile-picture-input').click()}
                    >
                      {profilePictureFile || profile?.profilePicture ? 'Change Photo' : 'Add Photo'}
                    </Button>

                    {(profilePictureFile || profile?.profilePicture) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={handleRemoveProfilePicture}
                      >
                        Remove
                      </Button>
                    )}
                  </HStack>

                  <Input
                    id="profile-picture-input"
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePictureChange}
                    display="none"
                  />

                  <Text fontSize="sm" color="gray.500" textAlign="center">
                    Supported formats: JPEG, PNG, WebP (max 5MB)
                  </Text>
                </VStack>
              </FormControl>

              <FormControl>
                <FormLabel>Display Name</FormLabel>
                <Input
                  value={editForm.displayName}
                  onChange={(e) => setEditForm(prev => ({ ...prev, displayName: e.target.value }))}
                  placeholder="Enter your display name"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Bio</FormLabel>
                <Input
                  value={editForm.bio}
                  onChange={(e) => setEditForm(prev => ({ ...prev, bio: e.target.value }))}
                  placeholder="Tell us about yourself"
                />
              </FormControl>

              <HStack spacing={3} w="full" justify="flex-end">
                <Button variant="ghost" onClick={onClose} isDisabled={uploadingPicture}>
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleSaveProfile}
                  isLoading={uploadingPicture}
                  loadingText="Saving..."
                >
                  Save Changes
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default UserProfile;
